/**
 * TemplateSelector Component
 * Quick template selection for offer forms
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/shared/lib/api";
import { Button, Badge } from "@/shared/components/ui";
import { ClipboardList, Star, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { getCreditTypeLabel } from "@/shared/lib/utils";
import type { OfferTemplate, CreditType } from "@/shared/types";

interface TemplateSelectorProps {
  creditType?: CreditType;
  onSelect: (template: OfferTemplate) => void;
  onManageClick?: () => void;
}

export function TemplateSelector({ creditType, onSelect, onManageClick }: TemplateSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.getTemplates(),
  });

  const templates = (data?.templates || []) as OfferTemplate[];

  // Filter templates by credit type if provided
  const filteredTemplates = creditType
    ? templates.filter((t) => t.type === creditType)
    : templates;

  // Sort: default templates first, then by usage count
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    return b.usageCount - a.usageCount;
  });

  // Show top 3 templates in collapsed view
  const visibleTemplates = isExpanded ? sortedTemplates : sortedTemplates.slice(0, 3);

  if (isLoading) {
    return (
      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Ładowanie szablonów...</span>
        </div>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-center">
        <ClipboardList className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600 mb-2">Brak szablonów ofert</p>
        {onManageClick && (
          <Button variant="outline" size="sm" onClick={onManageClick}>
            Utwórz pierwszy szablon
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
          <ClipboardList className="w-4 h-4" />
          Użyj szablonu:
        </span>
        {onManageClick && (
          <button
            type="button"
            onClick={onManageClick}
            className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
          >
            Zarządzaj szablonami
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {visibleTemplates.map((template) => (
          <button
            key={template.id}
            type="button"
            onClick={() => onSelect(template)}
            className="p-3 text-left bg-white border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all group"
          >
            <div className="flex items-start justify-between mb-1">
              <span className="font-medium text-gray-900 text-sm group-hover:text-primary-700 line-clamp-1">
                {template.name}
              </span>
              {template.isDefault && (
                <Star className="w-4 h-4 text-warning-500 flex-shrink-0" fill="currentColor" />
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Badge variant="default" className="text-xs py-0">
                {getCreditTypeLabel(template.type)}
              </Badge>
              {template.defaultRrso && (
                <span>~{template.defaultRrso}% RRSO</span>
              )}
            </div>
          </button>
        ))}
      </div>

      {sortedTemplates.length > 3 && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Zwiń ({sortedTemplates.length - 3} więcej)
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Pokaż więcej ({sortedTemplates.length - 3})
            </>
          )}
        </button>
      )}
    </div>
  );
}
